/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  CreateRemoteSessionIssuerForm,
  CreateRemoteSessionIssuerForm$Outbound,
  CreateRemoteSessionIssuerForm$outboundSchema,
} from "./createremotesessionissuerform.js";
import {
  ServerIdentityClientConfiguration,
  ServerIdentityClientConfiguration$Outbound,
  ServerIdentityClientConfiguration$outboundSchema,
} from "./serveridentityclientconfiguration.js";

/**
 * How to provide the OAuth client.
 */
export const ClientMode = {
  Auto: "auto",
  Existing: "existing",
  Manual: "manual",
} as const;
/**
 * How to provide the OAuth client.
 */
export type ClientMode = ClosedEnum<typeof ClientMode>;

/**
 * Auto mode only. cimd (the default) prefers a Client ID Metadata Document when the provider supports one and falls back to dynamic client registration; dcr always uses dynamic client registration.
 */
export const RegistrationMethod = {
  Cimd: "cimd",
  Dcr: "dcr",
} as const;
/**
 * Auto mode only. cimd (the default) prefers a Client ID Metadata Document when the provider supports one and falls back to dynamic client registration; dcr always uses dynamic client registration.
 */
export type RegistrationMethod = ClosedEnum<typeof RegistrationMethod>;

/**
 * A complete plan for configuring identity on one Remote MCP-backed MCP server. Exactly one of provider_id or create_provider is required. client_mode controls which client fields are accepted: existing requires existing_client_id and no client_configuration; auto and manual require client_configuration and no existing_client_id.
 */
export type CommitServerIdentityConfigurationForm = {
  /**
   * Configuration for a newly-created project remote-session client. Manual mode requires client_id. Auto mode forbids client_id and client_secret and uses scope, audience, and token_endpoint_auth_method as registration preferences.
   */
  clientConfiguration?: ServerIdentityClientConfiguration | undefined;
  /**
   * How to provide the OAuth client.
   */
  clientMode: ClientMode;
  /**
   * Form for creating a remote_session_issuer.
   */
  createProvider?: CreateRemoteSessionIssuerForm | undefined;
  /**
   * An existing visible remote-session client to link. Required only for existing mode.
   */
  existingClientId?: string | undefined;
  /**
   * The target MCP server. It must be backed directly by a Remote MCP source.
   */
  mcpServerId: string;
  /**
   * An existing Remote Identity Provider visible to the target project.
   */
  providerId?: string | undefined;
  /**
   * Auto mode only. cimd (the default) prefers a Client ID Metadata Document when the provider supports one and falls back to dynamic client registration; dcr always uses dynamic client registration.
   */
  registrationMethod?: RegistrationMethod | undefined;
  /**
   * Refuse a save if the user session issuer also serves another MCP server. Setup screens must set this to avoid changing shared sign-in bindings. Explicitly assign a dedicated issuer before retrying.
   */
  targetServerOnly?: boolean | undefined;
};

/** @internal */
export const ClientMode$outboundSchema: z.ZodMiniEnum<typeof ClientMode> = z
  .enum(ClientMode);

/** @internal */
export const RegistrationMethod$outboundSchema: z.ZodMiniEnum<
  typeof RegistrationMethod
> = z.enum(RegistrationMethod);

/** @internal */
export type CommitServerIdentityConfigurationForm$Outbound = {
  client_configuration?: ServerIdentityClientConfiguration$Outbound | undefined;
  client_mode: string;
  create_provider?: CreateRemoteSessionIssuerForm$Outbound | undefined;
  existing_client_id?: string | undefined;
  mcp_server_id: string;
  provider_id?: string | undefined;
  registration_method?: string | undefined;
  target_server_only?: boolean | undefined;
};

/** @internal */
export const CommitServerIdentityConfigurationForm$outboundSchema:
  z.ZodMiniType<
    CommitServerIdentityConfigurationForm$Outbound,
    CommitServerIdentityConfigurationForm
  > = z.pipe(
    z.object({
      clientConfiguration: z.optional(
        ServerIdentityClientConfiguration$outboundSchema,
      ),
      clientMode: ClientMode$outboundSchema,
      createProvider: z.optional(CreateRemoteSessionIssuerForm$outboundSchema),
      existingClientId: z.optional(z.string()),
      mcpServerId: z.string(),
      providerId: z.optional(z.string()),
      registrationMethod: z.optional(RegistrationMethod$outboundSchema),
      targetServerOnly: z.optional(z.boolean()),
    }),
    z.transform((v) => {
      return remap$(v, {
        clientConfiguration: "client_configuration",
        clientMode: "client_mode",
        createProvider: "create_provider",
        existingClientId: "existing_client_id",
        mcpServerId: "mcp_server_id",
        providerId: "provider_id",
        registrationMethod: "registration_method",
        targetServerOnly: "target_server_only",
      });
    }),
  );

export function commitServerIdentityConfigurationFormToJSON(
  commitServerIdentityConfigurationForm: CommitServerIdentityConfigurationForm,
): string {
  return JSON.stringify(
    CommitServerIdentityConfigurationForm$outboundSchema.parse(
      commitServerIdentityConfigurationForm,
    ),
  );
}
